import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Container,
  Typography,
  Paper,
  Box,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Fade,
  TextField,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useParams } from "react-router-dom";
import { apiService } from "../app/api";
import { handleAxiosError } from "../helper/errorHandler";
import BaseLayout from "../layout/BaseLayout";
import { getFeatureMeta } from "../helper/getFeatureMeta";
import SearchIcon from "@mui/icons-material/Search";

interface UserPermission {
  feature: string;
  usage_limit: number;
}

interface FeatureUsage {
  _id: string;
  total_used_limit: number;
  user_permission: UserPermission;
}

interface ClientUser {
  _id: string;
  name: string;
  email: string;
  feature_usages: FeatureUsage[];
}

const ClientUserUsage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<string | false>(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await apiService.get(
        `/client-user-usages?client_id=${id}&search=${searchTerm}&page=${
          page + 1
        }&limit=${rowsPerPage}`
      );
      setData(res.data || []);
      setTotalUsers(res.total || 0);
    } catch (err: any) {
      handleAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, page, rowsPerPage, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleAccordionChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const getProgressColor = (percent: number) => {
    if (percent < 50) return "success";
    if (percent < 80) return "warning";
    return "error";
  };

  if (loading) {
    return (
      <BaseLayout>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate(-1)}>
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography fontSize={26} fontWeight={700} textTransform="capitalize">
            {sessionStorage.getItem("client_name") || "Client"}'s User Usages
          </Typography>
        </Box>
      </Box>

      <Container sx={{ mt: 4 }}>
        <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <TextField
            placeholder="Type user email or name to search..."
            size="small"
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {data.length === 0 ? (
          <Typography textAlign="center" color="text.secondary">
            No users found.
          </Typography>
        ) : (
          data.map((user) => (
            <Card
              key={user._id}
              sx={{
                mb: 2,
                borderRadius: 3,
                boxShadow: 3,
                transition: "0.3s",
                "&:hover": { boxShadow: 5 },
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Accordion
                  expanded={expanded === user._id}
                  onChange={handleAccordionChange(user._id)}
                  sx={{ boxShadow: "none" }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor:
                        expanded === user._id
                          ? "action.hover"
                          : "background.paper",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box
                      flex={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                      {user.feature_usages.length === 0 ? (
                        <Chip label="No Usage" color="warning" size="small" />
                      ) : (
                        <Chip
                          label={`${user.feature_usages.length} features`}
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {user.feature_usages.length > 0 ? (
                      <Fade in={expanded === user._id}>
                        <Grid container spacing={2}>
                          {user.feature_usages.map((usage) => {
                            const meta = getFeatureMeta(
                              usage.user_permission.feature
                            );
                            const usageLimit =
                              usage.user_permission.usage_limit || 0;
                            const used = usage.total_used_limit || 0;
                            const usagePercent =
                              usageLimit > 0 ? (used / usageLimit) * 100 : 0;
                            const color = getProgressColor(usagePercent);

                            return (
                              <Grid item xs={12} md={6} key={usage._id}>
                                <Paper
                                  sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {meta?.display_name ||
                                      usage.user_permission.feature}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Used: {used.toLocaleString()} /{" "}
                                    {usageLimit.toLocaleString()}{" "}
                                    {meta?.unit || "N/A"}
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={usagePercent}
                                    color={color}
                                    sx={{ mt: 1, height: 8, borderRadius: 5 }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    ~ {usagePercent.toFixed(2)}% used
                                  </Typography>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Fade>
                    ) : (
                      <Typography color="text.secondary" sx={{ pl: 2 }}>
                        No feature usage data
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          ))
        )}
      </Container>

      {totalUsers > rowsPerPage && (
        <Box display="flex" justifyContent="center" mt={2}>
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </Box>
      )}
    </BaseLayout>
  );
};

export default ClientUserUsage;
